import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { useModuleAccess } from '@/hooks/useAccountModules';
import { TrackingPixelsCard } from '@/pages/admin/TrackingPixelsCard';
import { GoogleBusinessIntegrationCard } from '@/pages/admin/GoogleBusinessIntegrationCard';
import { ZernioIntegrationCard } from '@/pages/admin/ZernioIntegrationCard';
import { ProviderCard, offeredNativeProviders, type NativeProvider } from '@/pages/admin/MarketingProviderCard';
import {
  listMarketingIntegrations,
  PROVIDER_LABELS,
} from '@/services/marketingIntegrations.service';
import type { MarketingProvider, MarketingIntegrationRow } from '@/services/marketingIntegrations.service';
import { useMetaAdsEnabled } from '@/pages/marketing/metaAds';
import { SEARCH_CONSOLE_VISIBLE } from '@/pages/marketing/ads/searchConsoleVisibility';

export default function MarketingIntegrationsAdmin() {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();

  // Surface the OAuth-callback redirect outcome from the URL the provider
  // bounces us back to.
  useEffect(() => {
    const oauth = searchParams.get('oauth');
    const provider = searchParams.get('provider');
    const reason = searchParams.get('reason');
    if (!oauth) return;
    if (oauth === 'connected' && provider) {
      toast.success(`${PROVIDER_LABELS[provider as MarketingProvider] ?? provider} connected. Pick an account to finish.`);
      qc.invalidateQueries({ queryKey: ['admin', 'marketing-integrations'] });
    } else if (oauth === 'error') {
      toast.error(`Connection failed${reason ? `: ${reason}` : ''}`);
    }
    // Strip the params so a refresh doesn't re-toast.
    searchParams.delete('oauth');
    searchParams.delete('provider');
    searchParams.delete('reason');
    setSearchParams(searchParams, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'marketing-integrations'],
    queryFn: listMarketingIntegrations,
  });

  // Native connections only (SCRUM-2287): each Google product and Meta through
  // our own OAuth apps, in this order. A provider with no row yet shows as
  // "not connected".
  const { enabled: metaAdsEnabled } = useMetaAdsEnabled();
  const nativeRows = offeredNativeProviders(metaAdsEnabled).map((provider) =>
    (data ?? []).find((r) => r.provider === provider)
      ?? ({ provider, connectionStatus: 'not_connected', status: 'inactive' } as unknown as MarketingIntegrationRow),
  ) as Array<MarketingIntegrationRow & { provider: NativeProvider }>;
  const hasZernio = useModuleAccess('zernio').explicit;
  const hasGoogleBusiness = useModuleAccess('google business').explicit;

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Marketing Integrations"
        subtitle={`Connect Google Analytics, ${SEARCH_CONSOLE_VISIBLE ? 'Search Console, ' : ''}${metaAdsEnabled ? 'Google Ads and Meta Ads' : 'Google Ads'} directly: sign in with each, pick the account, done.`}
      />
      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Spinner /></div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            {nativeRows.map((row) => <ProviderCard key={row.provider} row={row} />)}
          </div>
          <TrackingPixelsCard rows={data ?? []} />
          {/* Above Zernio deliberately: Google Business is what a practice
              recognises and comes looking for; Zernio is the plumbing under it.
              Separate gates since 2026-09 — an account can hold one without the
              other. Google Business still needs the key, which is why the card
              says so when it is missing rather than hiding.

              The Zernio card therefore shows for EITHER module: Google Business
              tells the admin to add the key "below", and on a Google-only
              account that card would otherwise not be rendered, leaving the
              instruction pointing at nothing and the account unfixable from
              this screen. */}
          {hasGoogleBusiness && <GoogleBusinessIntegrationCard />}
          {(hasZernio || hasGoogleBusiness) && <ZernioIntegrationCard />}
        </div>
      )}
    </div>
  );
}
